import type { Locale } from '../../i18n/locales';
import { resolveHref } from './localization';

export const CAR_COMPARISON_FORM_URL = '/vertailu/autovakuutus/tiedot';

export const getCarComparisonLinkProps = (href?: string) =>
  href === CAR_COMPARISON_FORM_URL && /^https?:\/\//i.test(href)
    ? {
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {};

type ComparisonHrefOptions = {
  includeLegacyAutovakuutus?: boolean;
};

const comparisonStartPaths = new Set([
  '/vertailu',
  '/vertailu/autovakuutus',
  '/vertailu/autovakuutus/tiedot',
]);

const legacyComparisonCtaPaths = new Set(['/autovakuutus']);
const externalOrSpecialHrefPattern = /^(?:[a-zA-Z][a-zA-Z\d+.-]*:|\/\/)/;

const getHrefPathname = (href: string) => {
  const trimmed = href.trim();
  if (!trimmed) return '';

  if (externalOrSpecialHrefPattern.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
      const isProjectHost =
        parsed.hostname === 'vakuutuslaskuri.fi' ||
        parsed.hostname === 'www.vakuutuslaskuri.fi' ||
        parsed.hostname.endsWith('.run.app');
      if (!isProjectHost) return '';
      return parsed.pathname || '/';
    } catch {
      return '';
    }
  }

  const path = trimmed.match(/^([^?#]*)(.*)$/)?.[1] || trimmed;
  return path.startsWith('/') ? path : `/${path}`;
};

const normalizePath = (href: string) => {
  const pathname = getHrefPathname(href);
  if (!pathname) return '';
  return pathname.replace(/^\/(fi|sv|en)(?=\/|$)/, '').replace(/\/+$/, '') || '/';
};

export const mapCarComparisonHref = (
  href: string | undefined,
  options: ComparisonHrefOptions = {},
) => {
  if (!href?.trim()) return href;

  const normalizedPath = normalizePath(href);
  if (
    comparisonStartPaths.has(normalizedPath) ||
    (options.includeLegacyAutovakuutus && legacyComparisonCtaPaths.has(normalizedPath))
  ) {
    return CAR_COMPARISON_FORM_URL;
  }

  return href;
};

export const resolvePublicHref = (
  locale: Locale,
  href: string,
  options: ComparisonHrefOptions = {},
) => {
  const mappedHref = mapCarComparisonHref(href, options) || '';
  if (externalOrSpecialHrefPattern.test(mappedHref)) return mappedHref;
  return resolveHref(locale, mappedHref);
};
